import { ButtonLink, Heading5 } from '@blueprint/ui';
import { SaveOutlineSmall, ArrowRightSmall } from '@blueprint/icons';
import { ListingImage } from '@zoopla/listings/image';
import { ListingFeatureList } from '@zoopla/listings/features';
import { LifecycleTag } from '@zoopla/shared/components';
import {
    HeadingRow,
    StyledCenteringDiv
} from './SavedListingsWidgetMobile.styles';
import {
    trackClickSavedListingWidgetCard,
    trackClickSeeAllSavedListings
} from '../SavedListingsWidget.tracking';
import {
    TriggerWhenVisible,
    ListingCardMiniColumnWithShrinkBreakpoint,
    ListingCardMini,
    StyledListingCardMiniTitle,
    StyledListingCardMiniDetails,
    StyledListingCardMiniPrice,
    ListingCardMiniSkeleton
} from '@zoopla/listings/cards';
import { useListing } from '../graphql/Listing/useListing';
import { MappedEnrichedSavedPropertyId } from '../graphql/EnrichedSavedPropertyIds/useEnrichedSavedPropertyIds';

const SavedListingCardMobile = ({
    listingId,
    isTriggered
}: {
    listingId: string;
    isTriggered: boolean;
}) => {
    const listing = useListing(listingId, !isTriggered);

    if (!listing) {
        return <ListingCardMiniSkeleton />;
    }

    const {
        imagePreview,
        featurePreview,
        displayAddress,
        listingLinkHref,
        displayPrice,
        shortDisplayAddress,
        publicationStatus,
        section,
        statusSummary,
        tags
    } = listing;

    return (
        <ListingCardMini
            testId="saved-listing-card-mobile"
            listingLinkHref={listingLinkHref}
            trackClick={trackClickSavedListingWidgetCard}
        >
            <LifecycleTag
                publicationStatus={publicationStatus}
                section={section}
                statusSummary={statusSummary}
                tags={tags}
            />
            <ListingImage image={imagePreview} useImageLazyLoad={true} />
            <StyledListingCardMiniDetails
                data-testid={`mini-saved-property-card-details-${listingId}`}
            >
                <StyledListingCardMiniTitle>
                    {shortDisplayAddress || displayAddress}
                </StyledListingCardMiniTitle>
                <StyledListingCardMiniPrice>
                    {displayPrice}
                </StyledListingCardMiniPrice>
                <ListingFeatureList features={featurePreview} />
            </StyledListingCardMiniDetails>
        </ListingCardMini>
    );
};

export const SavedListingsWidgetMobile = ({
    listings
}: {
    listings: Array<MappedEnrichedSavedPropertyId>;
}) => {
    return (
        <div
            data-testid="saved-property-widget"
            aria-labelledby="saved-properties-widget-header"
        >
            <HeadingRow>
                <SaveOutlineSmall />
                <Heading5 size="6" id="saved-properties-widget-header">
                    Recently saved properties
                </Heading5>
            </HeadingRow>

            <ListingCardMiniColumnWithShrinkBreakpoint>
                {listings.map(({ listingId }) => {
                    return (
                        <TriggerWhenVisible triggerDelay={100} key={listingId}>
                            {(isTriggered: boolean) => {
                                return (
                                    <SavedListingCardMobile
                                        listingId={listingId}
                                        isTriggered={isTriggered}
                                    />
                                );
                            }}
                        </TriggerWhenVisible>
                    );
                })}
            </ListingCardMiniColumnWithShrinkBreakpoint>

            <StyledCenteringDiv>
                <ButtonLink
                    variant="tertiary"
                    href="/favourites/properties/"
                    data-testid="see-all-saved-properties"
                    onClick={trackClickSeeAllSavedListings}
                >
                    See all saved properties
                    <ArrowRightSmall />
                </ButtonLink>
            </StyledCenteringDiv>
        </div>
    );
};
